import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import TextProgress from './TextAnalyticsChart';

const TextAnalyticsContent = ({ textData, audioData }) => {
  const [activeTab, setActiveTab] = useState('grammer_score'); // Default active tab

  const toggleTab = (tabName) => {
    setActiveTab(activeTab === tabName ? null : tabName);
  };

  const HighlightedText = ({ text, fillerWordsIndex }) => {
    if (!text) return <p className="text-gray-600">No transcript available.</p>;

    const words = text.split(/(\s+)/); // Preserve spaces

    const highlightedText = words.map((word, index) => {
      const cleanedWord = word.toLowerCase().replace(/[^a-z0-9]/g, "");
      const isHighlighted = Object.keys(fillerWordsIndex || {}).includes(cleanedWord);

      return (
        <span
          key={index}
          className={`${isHighlighted ? 'font-bold text-red-600' : 'font-normal text-[#303972]'}`}
          style={{ fontFamily: 'sans-serif, Archivo' }}
        >
          {word}
        </span>
      );
    });

    return <p className="text-base leading-relaxed">{highlightedText}</p>;
  };

  // Render content for each active tab
  const renderTabContent = (tabName) => {
    if (!textData) {
      return (
        <div className="text-center h-full w-full flex flex-col items-center justify-center p-4">
          <img
            src="/images/No_Search_Result.svg"
            alt="No results"
            className="mx-auto mb-4"
            style={{ width: "200px", height: "auto" }}
          />
          <p className="text-[#8A9ABE]">
            No text analysis data available.
          </p>
        </div>
      );
    }

    const MAX_SCORE = 10;

    switch (tabName) {
      case 'grammer_score':
        return (
          <div className="flex items-center justify-center h-full w-full">
            <div className="flex items-center">
              {/* GRAPH */}
              <div className="flex-shrink-0">
                <TextProgress type="grammar_score" value={parseFloat(textData.parameter_score?.grammar_score)} />
              </div>

              {/* Graph text */}
              <div className="flex flex-col items-center text-right ml-10">
                <h4 className="text-lg font-semibold text-gray-700 mb-2">Grammar Score</h4>
                <p className="text-[#303972] text-xl font-bold">
                  {textData.parameter_score?.grammar_score !== undefined && textData.parameter_score?.grammar_score !== null
                    ? `${textData.parameter_score.grammar_score} / ${MAX_SCORE}`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        );
      case 'clarity_score':
        return (
          <div className="flex items-center justify-center h-full w-full">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TextProgress type="clarity_score" value={parseFloat(textData.parameter_score?.clarity_score)} />
              </div>
              <div className="flex flex-col items-center text-right ml-10">
                <h4 className="text-lg font-semibold text-gray-700 mb-2">Clarity Score</h4>
                <p className="text-[#303972] text-xl font-bold">
                  {textData.parameter_score?.clarity_score !== undefined && textData.parameter_score?.clarity_score !== null
                    ? `${textData.parameter_score?.clarity_score} / ${MAX_SCORE}`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        );
      case 'fluency_score':
        return (
          <div className="flex items-center justify-center h-full w-full">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TextProgress type="fluency_score" value={parseFloat(textData.parameter_score?.fluency_score)} />
              </div>
              <div className="flex flex-col items-center text-right ml-10">
                <h4 className="text-lg font-semibold text-gray-700 mb-2">Fluency Score</h4>
                {/* Note: Corrected `textData.parameter_score?.clarity_score` to `textData.parameter_score?.fluency_score` */}
                <p className="text-[#303972] text-xl font-bold">
                  {textData.parameter_score?.fluency_score !== undefined && textData.parameter_score?.fluency_score !== null
                    ? `${textData.parameter_score?.fluency_score} / ${MAX_SCORE}`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        );
      case 'filler_words':
        // Determine if there are filler words and transcript for rendering
        const hasFillerWords = textData.transcription && textData.filler_words?.filler_words_index &&
                               Object.keys(textData.filler_words.filler_words_index).length > 0;

        return (
          <div
            className={`flex flex-col ${hasFillerWords ? 'max-h-[55vh] overflow-y-auto' : ''}`}
          >
            {textData.transcription ? (
              hasFillerWords ? (
                <HighlightedText
                  text={textData.transcription}
                  fillerWordsIndex={textData.filler_words.filler_words_index}
                />
              ) : (
                <p className="text-gray-600">There are no filler words.</p>
              )
            ) : (
              <p className="text-gray-600">There are no filler words.</p>
            )}
          </div>
        );

      default:
        return <p className="p-4 text-center text-gray-600">Select a section to view details.</p>;
    }
  };

  const sections = [
    { id: "grammer_score", title: "Grammar Score" },
    { id: "clarity_score", title: "Clarity Score" },
    { id: "fluency_score", title: "Fluency Score" },
    { id: "filler_words", title: "Filler Words" },
  ];

  return (
    <div>
      {textData?.error ? (
        <div className='w-full h-[500px] flex items-center justify-center'>
          <div className="text-center h-full w-full flex flex-col items-center justify-center">
            <img
              src="/images/No_Search_Result.svg"
              alt="No results"
              className="mx-auto mb-4"
              style={{ width: "200px", height: "auto" }}
            />
            <p className="text-[#8A9ABE]">
              {textData.error}
            </p>
          </div>
        </div>
      ) : (
        <div className="accordion">
          {sections.map((section) => (
            <div className="accordion-item" key={section.id}>
              <div
                className="flex items-center justify-between cursor-pointer p-4"
                onClick={() => toggleTab(section.id)}
              >
                <span className="font-semibold text-[#303972] capitalize">{section.title}</span>
                <div className="flex items-center">
                  <div className="ml-6 p-1 border border-gray-300 rounded-md flex items-center justify-center">
                    {activeTab === section.id ? (
                      <FaChevronUp className="text-[#303972]" />
                    ) : (
                      <FaChevronDown className="text-[#303972]" />
                    )}
                  </div>
                </div>
              </div>

              {activeTab === section.id && (
                // Removed lg:h-[200px] and overflow-auto from here
                <div className="border-t border-gray-300 p-4 bg-[#F7F7F7]">
                  {renderTabContent(section.id)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TextAnalyticsContent;